import React, { ReactNode, CSSProperties } from "react";
import { useFormikContext } from "formik";
import Tooltip from "@mui/material/Tooltip";
import styles from "./FormGroup.module.css";

export interface FormGroupStyleClasses {
    root?: string;
    header?: string;
    title?: string;
    subtitle?: string;
    content?: string;
    error?: string;
    tooltip?: string;
}

interface FormGroupProps {
    name?: string;
    title?: string;
    subtitle?: string;
    tooltip?: string;
    tooltipPlacement?: "top" | "right" | "bottom" | "left";
    disabledTooltip?: string;
    children: ReactNode;
    required?: boolean;
    className?: string;
    style?: CSSProperties;
    size?: "small" | "medium";
    /** Optional content rendered on the right side of the title row. */
    headerAction?: ReactNode;
    classes?: FormGroupStyleClasses;
}

const FormGroup: React.FC<FormGroupProps> = ({
    name,
    title,
    subtitle,
    tooltip,
    tooltipPlacement = "top",
    disabledTooltip,
    children,
    required = false,
    className = "",
    style,
    size = "medium",
    headerAction,
    classes = {},
}) => {
    const formik = useFormikContext<Record<string, any>>();

    const hasError =
        name && formik && formik.touched && formik.errors
            ? name in formik.touched && !!formik.touched[name] && name in formik.errors && !!formik.errors[name]
            : false;

    const errorMessage = hasError && name && formik ? String(formik.errors[name]) : "";
    const titleClass =
        classes.title ??
        (size === "small"
            ? `${styles.titleSmall} form-group-title-small`
            : `${styles.titleMedium} form-group-title-medium`);
    const subtitleClass =
        classes.subtitle ??
        (size === "small"
            ? `${styles.subtitleSmall} form-group-subtitle-small`
            : `${styles.subtitleMedium} form-group-subtitle-medium`);
    const headerClass = classes.header ?? `${styles.header} form-group-header`;
    const contentClass = classes.content ?? `${styles.content} form-group-content`;
    const errorClass = classes.error ?? `${styles.error} form-group-error`;
    const tooltipClass = classes.tooltip ?? `${styles.tooltipIcon} tooltip-icon`;
    const rootClass = [
        classes.root ?? `${styles.root} form-group`,
        hasError ? `${styles.hasError} has-error` : "",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={rootClass} style={style} data-rw-control="form-group">
            {(title || tooltip || headerAction) && (
                <div className={headerClass} data-rw-part="header">
                    <div
                        style={
                            headerAction
                                ? { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }
                                : undefined
                        }
                    >
                        {title && (
                            <div className={titleClass} data-rw-part="title">
                                <h3>
                                    {title}
                                    {required && (
                                        <span className={`${styles.requiredAsterisk} required-asterisk`}>*</span>
                                    )}
                                </h3>
                                {tooltip && (
                                    <Tooltip title={tooltip} arrow placement={tooltipPlacement}>
                                        <span className={tooltipClass}>i</span>
                                    </Tooltip>
                                )}
                                {headerAction}
                            </div>
                        )}
                        {!title && headerAction}
                    </div>
                    {subtitle && (
                        <p className={subtitleClass} data-rw-part="subtitle">
                            {subtitle}
                        </p>
                    )}
                </div>
            )}
            {disabledTooltip ? (
                <Tooltip title={disabledTooltip} arrow>
                    <div className={contentClass} data-rw-part="content">
                        {children}
                    </div>
                </Tooltip>
            ) : (
                <div className={contentClass} data-rw-part="content">
                    {children}
                </div>
            )}

            {hasError && (
                <div className={errorClass} data-rw-part="error">
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export default FormGroup;
